import React, { useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Home, Settings, User, HelpCircle, ChevronRight, Package, DollarSign, Box, Users, Truck, FileText, Receipt, UserPlus, Printer, CreditCard } from 'lucide-react';
import { useAuth } from '../../../contexts/AuthContext';
import BusinessSwitcher from '../../../components/business/BusinessSwitcher';
import { useBusiness } from '../../../contexts/BusinessContext';
import { usePrinters } from '../../../hooks/usePrinters';

export function MenuScreen() {
  const navigate = useNavigate();
  const location = useLocation();
  const { signOut } = useAuth();
  const { business } = useBusiness();
  const { updatePrinterStatus } = usePrinters();

  // Función para imprimir prueba
  const handleTestPrint = async () => {
    try {
      console.log('Intentando imprimir con:', {
        printer_id: "3",
        business_id: business?.id
      });

      const response = await fetch('https://tibo-printer-service.fly.dev/print', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          printer_id: "3", // ID fijo que usa el printer client
          business_id: business?.id,
          content: "=== IMPRESION DE PRUEBA DESDE TIBO APP ===\n\n" +
                  `Business ID: ${business?.id}\n` +
                  `Business Name: ${business?.name}\n` +
                  `Fecha: ${new Date().toLocaleString()}\n\n` +
                  "=== Log: MenuScreen ===\n" +
                  "=== FIN DE PRUEBA ===\n\n\n\n",
          type: "delivery"
        })
      });
      
      const data = await response.json();
      console.log('Respuesta de impresión desde Tibo App:', data);
      
      if (data.error) {
        await updatePrinterStatus("3", false);
        throw new Error(data.error);
      }
      
      // Si llegamos aquí, la impresión fue exitosa
      await updatePrinterStatus("3", true);
      alert('¡Impresión de prueba enviada! Revisa la impresora.');
    } catch (error) {
      console.error('Error al imprimir desde Tibo App:', error);
      await updatePrinterStatus("3", false);
      alert(`Error al imprimir: ${error.message}`);
    }
  };

  useEffect(() => {
    if (location.state?.openSection === 'caja') {
      navigate('/caja');
    }
  }, [location.state, navigate]);

  const menuGroups = [
    {
      id: 'main-operations',
      items: [
        {
          id: 'caja',
          label: 'Caja',
          icon: <DollarSign className="w-5 h-5" />,
          onClick: () => navigate('/caja'),
          isAvailable: true
        },
        {
          id: 'deliveries',
          label: 'Entregas',
          icon: <Package className="w-5 h-5" />,
          onClick: () => navigate('/pending-deliveries'),
          isAvailable: true
        },
        {
          id: 'containers',
          label: 'Movimiento de Envases',
          icon: <Box className="w-5 h-5" />,
          onClick: () => {/* TODO: Implementar movimiento de envases */},
          isAvailable: false
        },
        {
          id: 'clients',
          label: 'Clientes',
          icon: <Users className="w-5 h-5" />,
          onClick: () => navigate('/clients'),
          isAvailable: true
        },
        {
          id: 'team',
          label: 'Equipo',
          icon: <UserPlus className="w-5 h-5" />,
          onClick: () => navigate('/team'),
          isAvailable: true
        },
        {
          id: 'suppliers',
          label: 'Proveedores',
          icon: <Truck className="w-5 h-5" />,
          onClick: () => {/* TODO: Implementar proveedores */},
          isAvailable: false
        },
        {
          id: 'settlements',
          label: 'Liquidaciones',
          icon: <FileText className="w-5 h-5" />,
          onClick: () => {/* TODO: Implementar liquidaciones */},
          isAvailable: false
        },
        {
          id: 'taxes',
          label: 'Impuestos',
          icon: <Receipt className="w-5 h-5" />,
          onClick: () => {/* TODO: Implementar impuestos */},
          isAvailable: false
        }
      ]
    },
    {
      id: 'settings',
      items: [
        {
          id: 'profile',
          label: 'Mi perfil',
          description: 'Configuración de cuenta, negocios y sesión',
          icon: <User className="w-5 h-5" />,
          onClick: () => navigate('/profile'),
          isAvailable: true
        },
        {
          id: 'subscription',
          label: 'Suscripción',
          description: 'Gestiona tu plan y facturación',
          icon: <CreditCard className="w-5 h-5" />,
          onClick: () => navigate('/subscription'),
          isAvailable: true
        },
        {
          id: 'settings',
          label: 'Configuración',
          description: 'Preferencias y ajustes generales',
          icon: <Settings className="w-5 h-5" />,
          onClick: () => navigate('/settings'),
          isAvailable: true
        },
        {
          id: 'test-print',
          label: 'Imprimir prueba',
          description: 'Envía una página de prueba a la impresora',
          icon: <Printer className="w-5 h-5" />,
          onClick: handleTestPrint,
          isAvailable: true
        },
        {
          id: 'help',
          label: 'Ayuda',
          description: 'Centro de ayuda y soporte',
          icon: <HelpCircle className="w-5 h-5" />,
          onClick: () => {/* TODO: Implementar ayuda */},
          isAvailable: false
        }
      ]
    }
  ];

  return (
    <div className="flex flex-col h-full bg-gray-50 dark:bg-gray-900">
      {/* Business Switcher */}
      <div className="bg-white dark:bg-gray-800 shadow dark:shadow-gray-700/30">
        <BusinessSwitcher />
      </div>

      {/* Menu Groups */}
      <div className="flex-1 p-6 space-y-8 overflow-y-auto">
        {menuGroups.map(group => (
          <div key={group.id} className="space-y-2">
            {group.title && (
              <h2 className="text-sm font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider px-4">
                {group.title}
              </h2>
            )}
            <div className="space-y-1">
              {group.items.map(item => (
                <button
                  key={item.id}
                  onClick={item.onClick}
                  className={`w-full flex items-center gap-3 p-4 rounded-lg text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors group ${
                    !item.isAvailable ? 'opacity-50 cursor-not-allowed' : ''
                  }`}
                  disabled={!item.isAvailable}
                >
                  {item.icon && (
                    <div className="flex-shrink-0 text-gray-500 dark:text-gray-400 group-hover:text-gray-900 dark:group-hover:text-white transition-colors">
                      {item.icon}
                    </div>
                  )}
                  <div className="flex-1 text-left">
                    <div className="font-medium group-hover:text-gray-900 dark:group-hover:text-white transition-colors flex items-center gap-2">
                      {item.label}
                      {!item.isAvailable && (
                        <span className="text-xs bg-gray-200 dark:bg-gray-700 text-gray-600 dark:text-gray-400 px-2 py-0.5 rounded">
                          Próximamente
                        </span>
                      )}
                    </div>
                    {item.description && (
                      <div className="text-sm text-gray-500 dark:text-gray-400">
                        {item.description}
                      </div>
                    )}
                  </div>
                  {item.component}
                </button>
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
} 